import React, { useEffect, useRef, useState, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

export default function AboutPage() {
  const sectionsRef = useRef([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isLoggedIn, logout, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const adminToken = localStorage.getItem('adminAuthToken');
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isAdminLoggedIn = !!adminToken && adminUser.role;

  const getDashboardLink = () => isAdminLoggedIn ? '/admin' : '/dashboard';
  const getDashboardLabel = () => {
    if (isAdminLoggedIn) return 'Admin Panel';
    return user?.name ? `Hi, ${user.name.split(' ')[0]}` : 'Dashboard';
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    logout();
    navigate('/', { replace: true });
    setTimeout(() => window.location.reload(), 100);
  };

  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1 }
    );
    sectionsRef.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addRef = (el) => {
    if (el && !sectionsRef.current.includes(el)) sectionsRef.current.push(el);
  };

  return (
    <>
      <SEOHead title="About LiteRent | Trusted Rent Access in Ibadan" description="Learn how LiteRent helps eligible renters in Ibadan move into verified homes with 30% upfront and spread the balance over time." canonical="https://literent.com.ng/about" />
    <main className="min-h-screen bg-[#fdfdfd] text-[#1a1a1a] overflow-x-hidden">
      <style>{`
        /* ── Font imports ── */
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600;1,700&family=Lora:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@400;500;600;700&display=swap');

        /*
          Cormorant Garamond — display headings: rich, editorial serif with
          beautiful italics and strong weight contrast.

          Lora — body prose: a reading-optimised serif with generous x-height,
          comfortable spacing, and warm personality. Excellent at 17–20px.

          Outfit — UI labels, nav, buttons, metadata: clean geometric sans
          that contrasts the serifs without fighting them.
        */

        .font-display  { font-family: 'Cormorant Garamond', Georgia, serif; }
        .font-prose    { font-family: 'Lora', Georgia, serif; }
        .font-ui       { font-family: 'Outfit', system-ui, sans-serif; }

        /* ── Typography scale ── */
        /* All body prose at a comfortable reading size */
        .prose-body {
          font-family: 'Lora', Georgia, serif;
          font-size: 1.125rem;      /* 18px */
          line-height: 1.85;        /* generous — critical for Lora */
          color: rgba(26,26,26,0.78);
          font-weight: 400;
          letter-spacing: 0.01em;
        }

        /* Long-form paragraphs on white backgrounds */
        .prose-body-lg {
          font-family: 'Lora', Georgia, serif;
          font-size: 1.1875rem;     /* 19px */
          line-height: 1.9;
          color: rgba(26,26,26,0.75);
          letter-spacing: 0.012em;
        }

        /* On dark/coloured backgrounds — slightly lighter weight */
        .prose-body-light {
          font-family: 'Lora', Georgia, serif;
          font-size: 1.125rem;
          line-height: 1.85;
          color: rgba(255,255,255,0.78);
          letter-spacing: 0.01em;
        }

        /* Section labels / pills */
        .label-ui {
          font-family: 'Outfit', system-ui, sans-serif;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.15em;
          text-transform: uppercase;
        }

        /* Nav & button text */
        .nav-ui {
          font-family: 'Outfit', system-ui, sans-serif;
          font-weight: 500;
          letter-spacing: 0.02em;
        }

        /* Card stat numbers */
        .stat-num {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-weight: 700;
          letter-spacing: -0.02em;
          line-height: 1;
        }

        /* ── Animation ── */
        .fade-up {
          opacity: 0;
          transform: translateY(36px);
          transition: opacity 0.75s cubic-bezier(.22,1,.36,1),
                      transform 0.75s cubic-bezier(.22,1,.36,1);
        }
        .fade-up.is-visible { opacity: 1; transform: translateY(0); }
        .delay-1 { transition-delay: 0.12s; }
        .delay-2 { transition-delay: 0.24s; }
        .delay-3 { transition-delay: 0.36s; }

        .stripe-bg {
          background: repeating-linear-gradient(
            -45deg, transparent, transparent 12px,
            rgba(165,194,80,0.07) 12px, rgba(165,194,80,0.07) 14px
          );
        }

        .value-card:hover .value-icon { transform: scale(1.18) rotate(-5deg); }
        .value-icon { transition: transform 0.35s cubic-bezier(.34,1.56,.64,1); display: inline-block; }

        /* Footer & small UI text */
        footer, .ui-small {
          font-family: 'Outfit', system-ui, sans-serif;
          font-size: 0.9375rem;
          line-height: 1.6;
        }
      `}</style>

      {/* ══ HEADER ══ */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#afc09e] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0">
              <img
                src="/Logo.jpg"
                alt="LiteRent Logo"
                className="h-12 sm:h-14 md:h-16 w-auto object-contain"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/180x60?text=LiteRent'; }}
              />
            </Link>

            <nav className="hidden lg:flex items-center space-x-8 xl:space-x-10">
              {[
                { to: '/', label: 'Home' },
                { to: '/how-it-works', label: 'How It Works' },
                { to: '/browse', label: 'Browse Houses' },
                { to: '/about', label: 'About Us', active: true },
                { to: '/contact', label: 'Contact Us' },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`nav-ui transition-colors ${link.active ? 'text-[#a5c250] font-semibold' : 'text-[#294c79] hover:text-[#1f3a5c] font-medium'}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex items-center space-x-4">
                {(isLoggedIn || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()}>
                      <button className={`nav-ui px-6 py-2.5 text-white rounded-full font-medium transition shadow-sm ${isAdminLoggedIn ? 'bg-red-600 hover:bg-red-700' : 'bg-[#294c79] hover:bg-[#1f3a5c]'}`}>
                        {getDashboardLabel()}
                      </button>
                    </Link>
                    <button onClick={handleLogout} className="nav-ui px-6 py-2.5 border-2 border-red-500 text-red-600 rounded-full font-medium hover:bg-red-50 transition">
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login"><button className="nav-ui px-6 py-2.5 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium hover:bg-[#294c79]/10 transition">Login</button></Link>
                    <Link to="/signup"><button className="nav-ui px-6 py-2.5 bg-[#a5c250] text-white rounded-full font-medium hover:bg-[#8bb83a] shadow-sm transition">Get Started</button></Link>
                  </>
                )}
              </div>
            </nav>

            <button
              className="lg:hidden p-2 rounded-md text-[#294c79] hover:bg-gray-100 focus:outline-none"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-[#afc09e] py-6 px-5">
            <div className="flex flex-col space-y-5">
              {[
                { to: '/', label: 'Home' },
                { to: '/how-it-works', label: 'How It Works' },
                { to: '/browse', label: 'Browse Houses' },
                { to: '/about', label: 'About Us', active: true },
                { to: '/contact', label: 'Contact Us' },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`nav-ui py-3 text-lg ${link.active ? 'text-[#a5c250] font-semibold' : 'text-[#294c79] font-medium'}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-6 border-t border-gray-200 flex flex-col space-y-4">
                {(isLoggedIn || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()} onClick={() => setIsMenuOpen(false)}>
                      <button className={`nav-ui w-full py-4 text-white rounded-full font-medium text-lg ${isAdminLoggedIn ? 'bg-red-600' : 'bg-[#294c79]'}`}>{getDashboardLabel()}</button>
                    </Link>
                    <button onClick={() => { handleLogout(); setIsMenuOpen(false); }} className="nav-ui w-full py-4 border-2 border-red-500 text-red-600 rounded-full font-medium text-lg">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}><button className="nav-ui w-full py-4 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium text-lg">Login</button></Link>
                    <Link to="/signup" onClick={() => setIsMenuOpen(false)}><button className="nav-ui w-full py-4 bg-[#a5c250] text-white rounded-full font-medium text-lg">Get Started</button></Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ══ HERO ══ */}
      <section className="relative py-20 sm:py-28 px-5 sm:px-8 overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#a5c250]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-[#294c79]/5 rounded-full blur-2xl pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div ref={addRef} className="fade-up">
            <span className="label-ui inline-block bg-[#e8f2d1] text-[#4a7a1e] px-4 py-2 rounded-full mb-6">
              About LiteRent
            </span>
          </div>
          <h1 ref={addRef} className="font-display fade-up delay-1 text-5xl sm:text-6xl md:text-7xl font-bold text-[#294c79] leading-[1.05] tracking-tight mb-8">
            Renting in Nigeria<br />
            <span className="italic text-[#a5c250]">should not hurt.</span>
          </h1>
          <p ref={addRef} className="fade-up delay-2 prose-body-lg max-w-2xl mx-auto">
            We built LiteRent because we have seen what happens when the system forces families to scrape together a full year of rent before they can even turn a key. That ends here.
          </p>
        </div>
      </section>

      {/* ══ WHO WE ARE ══ */}
      <section className="py-16 sm:py-24 px-5 sm:px-8 bg-[#294c79] relative overflow-hidden">
        <div className="stripe-bg absolute inset-0 pointer-events-none opacity-40" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <div ref={addRef} className="fade-up">
              <span className="label-ui inline-block border border-[#a5c250]/40 text-[#a5c250] px-4 py-2 rounded-full mb-6">
                Who We Are
              </span>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-white leading-[1.1] tracking-tight mb-7">
                A team that understands Ibadan's housing reality.
              </h2>
              <div className="space-y-5">
                <p className="prose-body-light">
                  LiteRent was founded by people who live and breathe the Nigerian rental market — people who have personally felt the sting of being told to pay one full year upfront or find somewhere else.
                </p>
                <p className="prose-body-light">
                  We are a property-technology company operating in <strong className="text-white font-semibold">Ibadan</strong>, one of the largest and most vibrant cities in Nigeria. Our work is simple: we connect tenants with verified, quality homes and give them a genuinely affordable way to move in.
                </p>
                <p className="prose-body-light">
                  No middlemen running away with deposits. No landlords demanding the impossible. Just a transparent process, a clean home, and a payment plan that respects your income and your dignity.
                </p>
              </div>
            </div>

            <div ref={addRef} className="fade-up delay-2">
              <div className="bg-white/5 border border-white/10 rounded-3xl p-8 sm:p-12">
                <div className="font-display text-8xl leading-none text-[#a5c250] opacity-25 mb-2 select-none">"</div>
                <p className="font-display text-2xl sm:text-3xl font-semibold text-white italic leading-[1.4] tracking-tight mb-8">
                  We believe a good home is not a luxury. It is a foundation — and every Nigerian deserves one.
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#a5c250] flex items-center justify-center text-white font-bold nav-ui text-sm">LR</div>
                  <div>
                    <p className="nav-ui text-white font-semibold text-sm">The LiteRent Team</p>
                    <p className="nav-ui text-white/50 text-xs mt-0.5">Ibadan, Nigeria</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ WHAT WE DO ══ */}
      <section className="py-16 sm:py-24 px-5 sm:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 sm:mb-20">
            <div ref={addRef} className="fade-up">
              <span className="label-ui inline-block bg-[#e8f2d1] text-[#4a7a1e] px-4 py-2 rounded-full mb-6">
                What We Do
              </span>
            </div>
            <h2 ref={addRef} className="font-display fade-up delay-1 text-4xl sm:text-5xl font-bold text-[#294c79] leading-[1.1] tracking-tight">
              We break the lump-sum barrier —<br className="hidden sm:block" />
              <span className="italic text-[#a5c250]"> for good.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: '🔍',
                title: 'We verify every home',
                body: `Before a property appears on LiteRent, our team physically inspects it. No fake listings, no misleading photos. What you see is what you move into — clean, safe, and exactly as described.`,
                delay: '',
              },
              {
                icon: '💰',
                title: 'We make move-in affordable',
                body: `Instead of paying one full year of rent upfront, LiteRent tenants move in with just 30% of their annual rent. The remaining 70% is broken into manageable monthly installments spread across 11 months.`,
                delay: 'delay-1',
              },
              {
                icon: '📋',
                title: 'We handle the paperwork',
                body: `From lease agreements to payment schedules, LiteRent creates a clear paper trail that protects both tenant and landlord. No verbal agreements. No grey areas. Everything is documented and transparent.`,
                delay: 'delay-2',
              },
              {
                icon: '🤝',
                title: 'We stay in your corner',
                body: `Our support does not end when you get your keys. LiteRent provides ongoing tenant support throughout your entire lease — from maintenance coordination to billing questions. We are always reachable.`,
                delay: 'delay-3',
              },
            ].map((item, i) => (
              <div key={i} ref={addRef} className={`value-card fade-up ${item.delay} group bg-[#fdfdfd] border border-[#e8f2d1] rounded-3xl p-8 sm:p-10 hover:shadow-xl hover:border-[#a5c250]/40 transition-all duration-300`}>
                <div className="value-icon text-5xl mb-6">{item.icon}</div>
                <h3 className="font-display text-2xl font-bold text-[#294c79] tracking-tight mb-4 leading-tight">{item.title}</h3>
                <p className="prose-body">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ THE IBADAN FOCUS ══ */}
      <section className="py-16 sm:py-24 px-5 sm:px-8 bg-[#f5f9ee]">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-3">
              <div ref={addRef} className="fade-up">
                <span className="label-ui inline-block bg-white border border-[#a5c250]/30 text-[#4a7a1e] px-4 py-2 rounded-full mb-6">
                  Our Home Base
                </span>
              </div>
              <h2 ref={addRef} className="font-display fade-up delay-1 text-4xl sm:text-5xl font-bold text-[#294c79] leading-[1.1] tracking-tight mb-7">
                Starting in Ibadan.<br />
                <span className="italic">Built for Nigeria.</span>
              </h2>
              <div ref={addRef} className="fade-up delay-2 space-y-5">
                <p className="prose-body">
                  Ibadan is one of the most populous cities in Africa — home to millions of working families, students, professionals, and entrepreneurs who all need quality, affordable housing. Yet the rental market has remained stubbornly traditional, demanding a full year of rent upfront with little transparency and no tenant protection.
                </p>
                <p className="prose-body">
                  LiteRent is changing that, starting right here. We have deliberately chosen to go deep in one city rather than spreading thin across many. We want to know every neighbourhood we operate in. We want our landlord partners to trust us. We want our tenants to feel like they are dealing with neighbours — not a distant corporation.
                </p>
                <p className="prose-body">
                  When Ibadan is right, we will take this model to the rest of Nigeria — city by city, the right way.
                </p>
              </div>
            </div>

            <div className="lg:col-span-2 grid grid-cols-1 gap-5 lg:pt-16" ref={addRef}>
              {[
                { num: '30%', label: 'is all you need to move in', sub: 'Pay the rest over 11 months' },
                { num: '100%', label: 'of listings are verified', sub: 'Physically inspected by our team' },
                { num: '₦0', label: 'hidden fees', sub: 'Everything is disclosed upfront' },
              ].map((s, i) => (
                <div key={i} className={`fade-up delay-${i + 1} bg-white rounded-2xl border border-[#e8f2d1] p-6 flex items-center gap-5 shadow-sm`}>
                  <div className="stat-num text-4xl text-[#a5c250] whitespace-nowrap">{s.num}</div>
                  <div>
                    <p className="nav-ui font-semibold text-[#294c79] leading-tight text-[0.95rem]">{s.label}</p>
                    <p className="nav-ui text-xs text-[#1a1a1a]/50 mt-1">{s.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ VISION + MISSION ══ */}
      <section className="py-16 sm:py-24 px-5 sm:px-8 bg-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8">
          <div ref={addRef} className="fade-up bg-[#f8fbf2] border border-[#dfecc0] rounded-[32px] p-8 sm:p-10 shadow-sm">
            <span className="label-ui inline-block bg-white border border-[#a5c250]/40 text-[#4a7a1e] px-4 py-2 rounded-full mb-6">Our Vision</span>
            <h3 className="font-display text-3xl sm:text-4xl font-bold text-[#294c79] tracking-tight mb-5">A future where decent rental housing is easier to access.</h3>
            <p className="prose-body">We see a Nigeria where renters no longer have to choose between quality housing and financial stability. LiteRent exists to build a rental market that is more transparent, more humane, and better aligned with how working people actually earn and live because we believe Renting should feel lighter.</p>
          </div>
          <div ref={addRef} className="fade-up delay-1 bg-[#294c79] text-white rounded-[32px] p-8 sm:p-10 shadow-sm relative overflow-hidden">
            <div className="absolute inset-0 stripe-bg opacity-20 pointer-events-none" />
            <div className="relative z-10">
              <span className="label-ui inline-block bg-white/10 border border-white/20 text-white px-4 py-2 rounded-full mb-6">Our Mission</span>
              <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-5">To help eligible renters in Ibadan move into verified homes with 30% upfront and spread the balance over time.</h3>
              <p className="prose-body-light">We combine verified listings, a clearer application process, and structured payment support so renters can move forward with confidence and landlords can work with more certainty.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ CORE VALUES ══ */}
      <section className="py-16 sm:py-24 px-5 sm:px-8 bg-[#f5f9ee]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 sm:mb-16">
            <span className="label-ui inline-block bg-white border border-[#a5c250]/30 text-[#4a7a1e] px-4 py-2 rounded-full mb-6">Core Values</span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#294c79] tracking-tight mb-4">What guides the LiteRent experience</h2>
            <p className="prose-body max-w-2xl mx-auto">These values shape how we treat renters, landlords, partners, and every decision we make as a company.</p>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
            {[
              { icon: '🛡️', title: 'Trust', body: 'We verify homes, explain terms clearly, and work hard to remove the fear and confusion that often come with renting.' },
              { icon: '🤝', title: 'Fairness', body: 'We believe access to housing should feel structured and respectful — not exploitative, hidden, or chaotic.' },
              { icon: '✨', title: 'Clarity', body: 'From pricing to application stages, we value simple communication so tenants always know what comes next.' },
              { icon: '🏘️', title: 'Local Depth', body: 'We start by knowing Ibadan properly — its neighbourhoods, landlords, renters, and the realities of the local market.' },
            ].map((value, index) => (
              <div key={value.title} ref={addRef} className={`fade-up delay-${(index % 3) + 1} bg-white border border-[#e8f2d1] rounded-[28px] p-7 shadow-sm hover:shadow-xl transition-all duration-300`}>
                <div className="w-14 h-14 rounded-2xl bg-[#e8f2d1] flex items-center justify-center text-2xl mb-5">{value.icon}</div>
                <h3 className="font-display text-2xl font-bold text-[#294c79] mb-3 tracking-tight">{value.title}</h3>
                <p className="prose-body">{value.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ MISSION STATEMENT ══ */}
      <section className="py-16 sm:py-24 px-5 sm:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div ref={addRef} className="fade-up relative inline-block mb-6 px-8">
            <div className="absolute -top-4 -left-2 font-display text-8xl text-[#a5c250]/20 select-none leading-none">"</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold text-[#294c79] italic leading-[1.3] tracking-tight relative z-10">
              We are building a smarter, more trusted rental experience for eligible renters who deserve a better way to move into quality housing.
            </h2>
            <div className="absolute -bottom-4 -right-2 font-display text-8xl text-[#a5c250]/20 select-none leading-none rotate-180">"</div>
          </div>
          <p ref={addRef} className="fade-up delay-1 prose-body max-w-xl mx-auto mt-12">
            We are not just a listings platform. We are a housing access company — and we measure our success in families housed, not just deals closed.
          </p>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section className="py-16 sm:py-24 px-5 sm:px-8 bg-[#294c79] relative overflow-hidden">
        <div className="absolute inset-0 stripe-bg opacity-30 pointer-events-none" />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 ref={addRef} className="font-display fade-up text-4xl sm:text-5xl font-bold text-white leading-[1.1] tracking-tight mb-6">
            Ready to find your home<br />in Ibadan?
          </h2>
          <p ref={addRef} className="fade-up delay-1 prose-body-light text-xl mb-10 max-w-lg mx-auto">
            Browse verified homes, use our rent calculator, and move in with just 30% upfront. The rest takes care of itself.
          </p>
          <div ref={addRef} className="fade-up delay-2 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/browse" className="nav-ui inline-block bg-[#a5c250] hover:bg-[#8bb83a] text-white font-bold px-10 py-4 rounded-full text-lg transition shadow-lg active:scale-95">
              Browse Homes →
            </Link>
            <Link to="/how-it-works" className="nav-ui inline-block bg-white/10 hover:bg-white/20 border border-white/20 text-white font-medium px-10 py-4 rounded-full text-lg transition active:scale-95">
              How It Works
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
    </>
  );
}